/**
 * Dev-only QA switch (CATALOGUE_QA_MASTERS). Lists master-script ids that are usable although `approvedOn` is not set,
 * so test books can be made before a story goes live. It only ever applies on the DEV Supabase project: any other host
 * (prod, look-alikes, missing or malformed URL) gives an empty set.
 *
 * Import-free on purpose: lib/picture-book/templates.ts reaches client bundles and workers. In the browser the var is
 * not NEXT_PUBLIC, so process.env has no value and the client sees no change.
 */
export type QaEnv = Record<string, string | undefined>;

/** The dev Supabase project host (prod is ykpdq...). Anchored: no look-alike matches. */
export const DEV_SUPABASE_HOST: RegExp = /^jcxku[a-z0-9]*\.supabase\.co$/;

const MAX_IDS = 20;
const MAX_ID_LEN = 100;
const EMPTY: ReadonlySet<string> = new Set<string>();

let logged = false;

function defaultEnv(): QaEnv {
  return typeof process !== 'undefined' && process.env ? (process.env as QaEnv) : {};
}

function hostOf(raw: string | undefined): string | null {
  if (!raw) return null;
  try {
    const u = new URL(raw.trim());
    if (u.protocol !== 'https:' || u.username || u.password || u.port) return null;
    return u.hostname.toLowerCase();
  } catch {
    return null;
  }
}

/** Ids in CATALOGUE_QA_MASTERS (comma list, trimmed, max 20, each at most 100 chars), only on the dev Supabase host; else empty. */
export function qaMasterIds(env: QaEnv = defaultEnv()): ReadonlySet<string> {
  const list = env.CATALOGUE_QA_MASTERS;
  if (!list || !list.trim()) return EMPTY;
  // Every Supabase URL that is set must be the dev host: the browser client reads NEXT_PUBLIC_SUPABASE_URL but the
  // server admin client prefers SUPABASE_URL, so a mixed env (one dev, one prod) must never switch QA masters on.
  const urls = [env.NEXT_PUBLIC_SUPABASE_URL, env.SUPABASE_URL].filter((u) => u !== undefined && u !== '');
  if (urls.length === 0) return EMPTY;
  for (const u of urls) {
    const host = hostOf(u);
    if (!host || !DEV_SUPABASE_HOST.test(host)) return EMPTY;
  }
  const ids = list
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && s.length <= MAX_ID_LEN)
    .slice(0, MAX_IDS);
  const set = new Set(ids);
  if (set.size > 0 && !logged && typeof window === 'undefined') {
    logged = true;
    console.log(`[CATALOGUE-QA] dev-only masters active: ${[...set].join(', ')}`);
  }
  return set;
}
